import { useRef, useState } from "react";

import { toggleValue, union } from "common/utils/sets";
import { useSearchState } from "common/router/hooks";

import LoadingPage from "client/common/components/loading/LoadingPage";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";
import Markdown from "client/common/components/content/Markdown";
import Page from "client/common/components/layouts/Page";
import Subheader from "client/common/components/layouts/Subheader";
import Body from "client/common/components/layouts/Body";
import { ModalStatus } from "client/common/types";
import Accordion from "client/common/components/interactive/Accordion";
import { Paths } from "client/routes";

import SupportOrderStatusModal from "./SupportOrderStatusModal";
import SupportTile from "./SupportTile";
import { IListSupportItem, SetModalVisibility } from "../types";
import { useSupportSectionsQuery } from "../hooks";
import SupportSearchInput from "./SupportSearchInput";
import { catchallItems, catchallSection } from "../constants";

import styles from "../styles/SupportPage.module.less";

type SupportRefs = {
  [id: number]: HTMLDivElement | null;
};

const SupportPage = () => {
  const [search, setSearch] = useSearchState<Paths["support"]["helpCenter"]>({
    orderStatusModalIsVisible: ModalStatus.Closed,
  });
  const setModalVisibility: SetModalVisibility = (param, visibility) =>
    setSearch({ ...search, [param]: visibility });

  const { data: supportSections, isLoading, error } = useSupportSectionsQuery();

  const [openIds, setOpenIds] = useState(
    new Set<string | number>(catchallItems.map((item) => item.id)),
  );

  const refs = useRef<SupportRefs>({});

  const handleRef = (id: number) => (ref: HTMLDivElement | null) => {
    // Assign the new ref to the lookup
    refs.current[id] = ref;
  };

  const handleSelect = (item: IListSupportItem) => {
    setOpenIds((ids) => union(ids, [item.id]));

    // Smooth scroll the element into view
    const element = refs.current[item.id];
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error || !supportSections) {
    return <FetchErrorPage error={error} entityName="support" />;
  }

  return (
    <Page>
      <div className={styles.gradient} />
      <div className={styles.background} />
      <Subheader
        title="How can we help you?"
        description="Need something? We're here to help. Search our FAQs for answers"
        renderActions={() => <SupportSearchInput onSelect={handleSelect} />}
        classNames={{ container: styles.subheader }}
      />
      <Body className={styles.main}>
        <div className={styles.sections}>
          {[...supportSections, catchallSection].map((section) => (
            <div className={styles.section} key={section.id}>
              <div className={styles.info}>
                <div className={styles.title}>{section.title}</div>
              </div>
              <div className={styles.items}>
                <Accordion
                  onSelect={(item) => setOpenIds((ids) => toggleValue(ids, item.key))}
                  getIsOpen={(item) => openIds.has(item.key)}
                  items={section.items.map((item) => ({
                    key: item.id,
                    label: item.title,
                    content: (
                      <div className={styles.item} ref={handleRef(item.id)}>
                        <Markdown markdown={item.body} />
                        <div className={styles.tiles}>
                          {item.tiles.map((tile) => (
                            <SupportTile
                              key={tile.id}
                              kind={tile.kind}
                              onSetModalVisibility={setModalVisibility}
                            />
                          ))}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </div>
            </div>
          ))}
        </div>
        <SupportOrderStatusModal
          visible={search.orderStatusModalIsVisible === ModalStatus.Open}
          onClose={() => {
            setSearch({
              ...search,
              orderStatusModalIsVisible: ModalStatus.Closed,
              orderStatusInitialOrderNumber: "",
            });
          }}
          initialValues={{ display_number: search.orderStatusInitialOrderNumber ?? "" }}
        />
      </Body>
    </Page>
  );
};

export default SupportPage;
